import React, { useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import styled, { css } from 'styled-components';

import { Show } from '../../../show';

import PlusCircled from './assets/plusCircled.svg?react';
import MinusCircled from './assets/minusCircled.svg?react';
import Edit from './assets/edit.svg?react';
import ExclamationCircle from './assets/exclamation-circle.svg?react';

import GridContainer from '../../../layouts/components/grid-container';
import OverflowContainer from '../../../layouts/components/overflow-container';
import FluidContainer from '../../../layouts/components/fluid-container';

import HintBox from '../../../hint-box';
import Spacer from '../../../spacer';
import Button from '../../../button';
import IconButton from '../../../icon-button';
import MathContent from '../../../math-content';
import Stack from '../../../stack';
import { Body } from '../../../typography';

import EditableBox from '../checker-step/editable-box';
import NotEditableBox from '../checker-step/not-editable-box';

import { AND, getCdesCorrection, isCdeNotEmpty } from '../../../../lib/checker';
import { CORRECT, MISSING, WRONG } from '../../../../lib/exercise';
import { useOnClickOutside } from '../../../../lib/hooks';

export const EditIcon = styled(Edit)`
    ${({ theme }) => css`
        fill: ${theme.colors['text-myzanichelli-blue-main']};

        &:hover {
            fill: ${theme.colors['text-myzanichelli-blue-dark']};
        }
    `}
`;

const ExclamationIcon = styled(ExclamationCircle)`
    height: 18px;
    width: 18px;
    ${({ theme }) => css`
        fill: ${theme.colors['surface-grey-800']};
    `}
`;

export const CdeBody = styled(Body)`
    ${({ isOpen }) => css`
        display: ${isOpen ? 'block' : 'none'};
    `}
`;

const CdeContainer = ({ children, ...rest }) => {
    return (
        <FluidContainer paddingY="xs" paddingX="small" alignItems="center" justifyContent="space-between" {...rest}>
            {children}
        </FluidContainer>
    );
};

CdeContainer.propTypes = {
    children: PropTypes.node,
};

const VALIDATION_COLORS = {
    [WRONG]: 'error-red-main',
    [CORRECT]: 'success-green-main',
    [MISSING]: 'surface-grey-200',
    false: 'warning-yellow-lighter',
};

const CDE_CHILD_COLORS = {
    true: 'surface-grey-050',
    false: 'warning-yellow-lighter',
};

const hintMessage = {
    [true]: 'Scrivi una condizione di esistenza. Se devi inserirne più di una usa "Aggiungi condizione". Quando hai terminato usa "Controlla e salva" per correggere.',
    [false]: 'Scrivi una condizione di esistenza. Se devi inserirne più di una usa "Aggiungi condizione".',
};

const AccordionContentStack = ({ cdes, cdeCorrection, existenceConditions, correctionStatus }) => {
    const filterCdes = cdes.filter(isCdeNotEmpty).map((cde) => cde.value);

    if (!!correctionStatus && cdeCorrection === MISSING) {
        return (
            <Stack direction="horizontal" gap="xs">
                <strong>{`Mancante:`}</strong>
                <MathContent>{existenceConditions.join(`&nbsp; ${AND} &nbsp;`)}</MathContent>
            </Stack>
        );
    } else {
        return <MathContent parseContent={false}>{filterCdes.join(`&nbsp; ${AND} &nbsp;`)}</MathContent>;
    }
};

AccordionContentStack.propTypes = {
    cdes: PropTypes.array,
    cdeCorrection: PropTypes.string,
    existenceConditions: PropTypes.array,
    correctionStatus: PropTypes.bool,
};

const ExistenceConditions = ({ openedCde, cdes, correctionStatus, exercise, isCorrectable, onAdd, onChange, onDelete, onOpen }) => {
    const [hintBoxIsOpen, setHintBoxIsOpen] = useState(true);
    const [isAccordionOpen, setAccordionIsOpen] = useState(true);
    const [showValidation, setShowValidation] = useState(false);

    useEffect(() => {
        if (correctionStatus) {
            setShowValidation(!!correctionStatus);
            setAccordionIsOpen(false);
        }
    }, [correctionStatus]);

    const cdeContainer = useRef();
    useOnClickOutside(cdeContainer, () => {
        setAccordionIsOpen(false);
    });

    const cdeCorrection = getCdesCorrection(cdes);

    return (
        <>
            <Show when={!correctionStatus}>
                <Spacer size="base" />
                <HintBox
                    label={hintMessage[isCorrectable]}
                    isOpen={hintBoxIsOpen}
                    exerciseType={exercise.typeLabel}
                    toggleHintBox={() => setHintBoxIsOpen(!hintBoxIsOpen)}
                />
            </Show>

            <Spacer size="base" />

            <OverflowContainer bg="none" ref={cdeContainer}>
                <CdeContainer
                    boxShadow="shadow-02dp"
                    borderRadius={4}
                    borderLeftWidth={8}
                    borderLeftStyle="solid"
                    borderLeftColor={VALIDATION_COLORS[showValidation && cdeCorrection]}
                >
                    <Stack direction="horizontal" gap="xs">
                        <AccordionContentStack
                            cdeCorrection={cdeCorrection}
                            cdes={cdes}
                            existenceConditions={exercise.existenceConditions}
                            correctionStatus={correctionStatus}
                        />
                    </Stack>

                    <Show when={!correctionStatus}>
                        <IconButton type="tertiary" size="xsmall" onClick={() => setAccordionIsOpen(!isAccordionOpen)}>
                            <Show when={isAccordionOpen}>
                                <MinusCircled fill="#424548" />
                            </Show>
                            <Show when={!isAccordionOpen}>
                                <PlusCircled fill="#424548" />
                            </Show>
                        </IconButton>
                    </Show>

                    <Show when={!!correctionStatus && cdeCorrection === MISSING}>
                        <ExclamationIcon />
                    </Show>
                </CdeContainer>
                <Spacer size="xs" />

                <CdeBody isOpen={isAccordionOpen}>
                    {cdes.map((cde, index) => {
                        if (index !== openedCde) {
                            return (
                                <FluidContainer
                                    key={index}
                                    padding="small"
                                    justifyContent="space-between"
                                    bg={CDE_CHILD_COLORS[showValidation]}
                                    borderRadius={4}
                                >
                                    <NotEditableBox
                                        content={`&nbsp;&nbsp; ${cde.value}`}
                                        isValidated={false}
                                        onClick={() => {
                                            onOpen(index);
                                        }}
                                    />
                                </FluidContainer>
                            );
                        }

                        return (
                            <React.Fragment key={index}>
                                <GridContainer
                                    key={index}
                                    paddingY={['xxs', 'small']}
                                    paddingX="small"
                                    gridGap="small"
                                    templateColumns="minmax(0,1fr) auto"
                                    bg={CDE_CHILD_COLORS[showValidation]}
                                    borderRadius={4}
                                >
                                    <EditableBox
                                        content={cde.value}
                                        onClick={() => {
                                            setShowValidation(false);
                                            onDelete(index);
                                        }}
                                        onChange={(value) => {
                                            setShowValidation(false);
                                            onChange(index, value);
                                        }}
                                        onConfirm={() => {
                                            setShowValidation(false);
                                            onAdd(index);
                                        }}
                                        isDisabled={cdes.length === 1}
                                    />
                                </GridContainer>

                                <Spacer size="small" />

                                <Body bg={CDE_CHILD_COLORS[showValidation]}>
                                    <CdeContainer paddingY={['xxs', 'small']} paddingX="regular">
                                        <Button
                                            type="tertiary"
                                            size="small"
                                            disabled={!cde.value}
                                            onClick={() => {
                                                onAdd(index);
                                            }}
                                        >
                                            {'Aggiungi condizione'}
                                        </Button>

                                        <Show when={isCorrectable}>
                                            <Button
                                                type="tertiary"
                                                size="small"
                                                onClick={() => {
                                                    if (cde.value) {
                                                        onAdd(index);
                                                    }
                                                    setShowValidation(true);
                                                    setAccordionIsOpen(false);
                                                }}
                                            >
                                                {'Controlla e salva'}
                                            </Button>
                                        </Show>
                                    </CdeContainer>
                                </Body>
                                <Spacer size="small" />
                            </React.Fragment>
                        );
                    })}
                </CdeBody>
            </OverflowContainer>
        </>
    );
};

ExistenceConditions.propTypes = {
    openedCde: PropTypes.number,
    cdes: PropTypes.array,
    correctionStatus: PropTypes.object,
    exercise: PropTypes.object,
    isCorrectable: PropTypes.bool,
    onAdd: PropTypes.func,
    onChange: PropTypes.func,
    onDelete: PropTypes.func,
    onOpen: PropTypes.func,
    onReplace: PropTypes.func,
};

export default ExistenceConditions;
